// game/world.ts — tile grid, circle-vs-tile collision with sliding, a BFS flow
// field toward the player, and a uniform spatial hash for entity queries.

import type { Vec, ZonePalette } from '../types'
import { spriteCanvas } from '../engine/sprites'
import type { Rng } from '../engine/rng'

export const TILE = 32

export const WALL = '#'
export const FLOOR = '.'

/** Glyphs that are solid unless a room rule opens them. */
function baseSolid(g: string): boolean {
  return g === '#'
}

export class World {
  w = 0
  h = 0
  glyphs: string[] = []
  solid = new Uint8Array(0)
  layer: HTMLCanvasElement | null = null

  // flow field
  private dist = new Int32Array(0)
  private flowX = new Float32Array(0)
  private flowY = new Float32Array(0)
  private queue = new Int32Array(0)
  private flowTimer = 0
  private flowTileIndex = -1
  flowValid = false

  get pixelW(): number {
    return this.w * TILE
  }
  get pixelH(): number {
    return this.h * TILE
  }

  load(map: readonly string[]): void {
    // Tile width is measured in *tiles*, not characters: 'G0'/'E1' markers are
    // two characters that occupy one tile (see rooms.ts parser).
    const rows: string[][] = []
    let maxW = 0
    for (const line of map) {
      const cells: string[] = []
      for (let i = 0; i < line.length; i++) {
        const c = line[i]
        if ((c === 'G' || c === 'E') && i + 1 < line.length && line[i + 1] >= '0' && line[i + 1] <= '9') {
          cells.push(c + line[i + 1])
          i++
        } else {
          cells.push(c)
        }
      }
      if (cells.length > maxW) maxW = cells.length
      rows.push(cells)
    }
    this.h = rows.length
    this.w = maxW
    const n = this.w * this.h
    this.glyphs = new Array(n)
    this.solid = new Uint8Array(n)
    for (let y = 0; y < this.h; y++) {
      const row = rows[y]
      for (let x = 0; x < this.w; x++) {
        const g = x < row.length ? row[x] : '#'
        const i = y * this.w + x
        this.glyphs[i] = g
        this.solid[i] = baseSolid(g) ? 1 : 0
      }
    }
    if (this.dist.length !== n) {
      this.dist = new Int32Array(n)
      this.flowX = new Float32Array(n)
      this.flowY = new Float32Array(n)
      this.queue = new Int32Array(n)
    }
    this.flowValid = false
    this.flowTimer = 0
    this.flowTileIndex = -1
  }

  index(tx: number, ty: number): number {
    return ty * this.w + tx
  }

  inBounds(tx: number, ty: number): boolean {
    return tx >= 0 && ty >= 0 && tx < this.w && ty < this.h
  }

  isWall(tx: number, ty: number): boolean {
    if (!this.inBounds(tx, ty)) return true
    return this.solid[ty * this.w + tx] === 1
  }

  setSolid(tx: number, ty: number, s: boolean): void {
    if (!this.inBounds(tx, ty)) return
    this.solid[ty * this.w + tx] = s ? 1 : 0
    this.flowValid = false
  }

  glyphAt(tx: number, ty: number): string {
    if (!this.inBounds(tx, ty)) return '#'
    return this.glyphs[ty * this.w + tx]
  }

  glyphAtPoint(p: Vec): string {
    return this.glyphAt(Math.floor(p.x / TILE), Math.floor(p.y / TILE))
  }

  solidAtPoint(x: number, y: number): boolean {
    return this.isWall(Math.floor(x / TILE), Math.floor(y / TILE))
  }

  /** Is a circle of radius r free of walls at this position? */
  circleFree(x: number, y: number, r: number): boolean {
    const tx0 = Math.floor((x - r) / TILE)
    const tx1 = Math.floor((x + r) / TILE)
    const ty0 = Math.floor((y - r) / TILE)
    const ty1 = Math.floor((y + r) / TILE)
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        if (this.isWall(tx, ty)) return false
      }
    }
    return true
  }

  // -------------------------------------------------------------- movement

  /** Axis-separated move so bodies slide along walls instead of sticking. */
  moveCircle(pos: Vec, dx: number, dy: number, r: number): boolean {
    let hit = false
    if (dx !== 0) {
      pos.x += dx
      if (this.pushOutX(pos, r, dx)) hit = true
    }
    if (dy !== 0) {
      pos.y += dy
      if (this.pushOutY(pos, r, dy)) hit = true
    }
    const maxX = this.pixelW - r - 0.01
    const maxY = this.pixelH - r - 0.01
    if (pos.x < r) {
      pos.x = r
      hit = true
    } else if (pos.x > maxX) {
      pos.x = maxX
      hit = true
    }
    if (pos.y < r) {
      pos.y = r
      hit = true
    } else if (pos.y > maxY) {
      pos.y = maxY
      hit = true
    }
    return hit
  }

  private pushOutX(pos: Vec, r: number, dir: number): boolean {
    const ty0 = Math.floor((pos.y - r + 0.5) / TILE)
    const ty1 = Math.floor((pos.y + r - 0.5) / TILE)
    if (dir > 0) {
      const tx = Math.floor((pos.x + r) / TILE)
      for (let ty = ty0; ty <= ty1; ty++) {
        if (this.isWall(tx, ty)) {
          pos.x = tx * TILE - r - 0.01
          return true
        }
      }
    } else {
      const tx = Math.floor((pos.x - r) / TILE)
      for (let ty = ty0; ty <= ty1; ty++) {
        if (this.isWall(tx, ty)) {
          pos.x = (tx + 1) * TILE + r + 0.01
          return true
        }
      }
    }
    return false
  }

  private pushOutY(pos: Vec, r: number, dir: number): boolean {
    const tx0 = Math.floor((pos.x - r + 0.5) / TILE)
    const tx1 = Math.floor((pos.x + r - 0.5) / TILE)
    if (dir > 0) {
      const ty = Math.floor((pos.y + r) / TILE)
      for (let tx = tx0; tx <= tx1; tx++) {
        if (this.isWall(tx, ty)) {
          pos.y = ty * TILE - r - 0.01
          return true
        }
      }
    } else {
      const ty = Math.floor((pos.y - r) / TILE)
      for (let tx = tx0; tx <= tx1; tx++) {
        if (this.isWall(tx, ty)) {
          pos.y = (ty + 1) * TILE + r + 0.01
          return true
        }
      }
    }
    return false
  }

  /** Bresenham wall check — used for "can I see/shoot the player". */
  lineOfSight(x0: number, y0: number, x1: number, y1: number): boolean {
    const steps = Math.ceil(Math.hypot(x1 - x0, y1 - y0) / (TILE * 0.5))
    if (steps <= 0) return true
    const sx = (x1 - x0) / steps
    const sy = (y1 - y0) / steps
    let x = x0
    let y = y0
    for (let i = 0; i < steps; i++) {
      x += sx
      y += sy
      if (this.solidAtPoint(x, y)) return false
    }
    return true
  }

  // ------------------------------------------------------------ flow field

  /** Recomputed at most every 200ms, or immediately if the player changed tile. */
  updateFlow(px: number, py: number, dt: number, force = false): void {
    this.flowTimer -= dt
    const tx = Math.max(0, Math.min(this.w - 1, Math.floor(px / TILE)))
    const ty = Math.max(0, Math.min(this.h - 1, Math.floor(py / TILE)))
    const idx = ty * this.w + tx
    if (!force && this.flowValid && this.flowTimer > 0) return
    this.flowTimer = 0.2
    this.flowTileIndex = idx
    this.computeFlow(tx, ty)
  }

  private computeFlow(tx: number, ty: number): void {
    const w = this.w
    const h = this.h
    const n = w * h
    const dist = this.dist
    dist.fill(-1)

    let start = ty * w + tx
    if (this.solid[start] === 1) {
      // player is inside geometry (shouldn't happen) — seed the nearest floor
      let best = -1
      let bestD = Infinity
      for (let i = 0; i < n; i++) {
        if (this.solid[i] === 1) continue
        const x = i % w
        const y = (i / w) | 0
        const d = (x - tx) * (x - tx) + (y - ty) * (y - ty)
        if (d < bestD) {
          bestD = d
          best = i
        }
      }
      if (best < 0) {
        this.flowValid = false
        return
      }
      start = best
    }

    const q = this.queue
    let head = 0
    let tail = 0
    dist[start] = 0
    q[tail++] = start
    while (head < tail) {
      const cur = q[head++]
      const d = dist[cur] + 1
      const cx = cur % w
      const cy = (cur / w) | 0
      if (cx > 0) {
        const i = cur - 1
        if (this.solid[i] === 0 && dist[i] < 0) {
          dist[i] = d
          q[tail++] = i
        }
      }
      if (cx < w - 1) {
        const i = cur + 1
        if (this.solid[i] === 0 && dist[i] < 0) {
          dist[i] = d
          q[tail++] = i
        }
      }
      if (cy > 0) {
        const i = cur - w
        if (this.solid[i] === 0 && dist[i] < 0) {
          dist[i] = d
          q[tail++] = i
        }
      }
      if (cy < h - 1) {
        const i = cur + w
        if (this.solid[i] === 0 && dist[i] < 0) {
          dist[i] = d
          q[tail++] = i
        }
      }
    }

    // Steepest descent, 8-way, refusing to cut solid corners.
    const fx = this.flowX
    const fy = this.flowY
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x
        fx[i] = 0
        fy[i] = 0
        if (this.solid[i] === 1 || dist[i] < 0) continue
        let bestD = dist[i]
        let bx = 0
        let by = 0
        for (let oy = -1; oy <= 1; oy++) {
          const ny = y + oy
          if (ny < 0 || ny >= h) continue
          for (let ox = -1; ox <= 1; ox++) {
            if (ox === 0 && oy === 0) continue
            const nx = x + ox
            if (nx < 0 || nx >= w) continue
            const j = ny * w + nx
            if (this.solid[j] === 1 || dist[j] < 0) continue
            if (ox !== 0 && oy !== 0) {
              if (this.solid[y * w + nx] === 1 || this.solid[ny * w + x] === 1) continue
            }
            if (dist[j] < bestD) {
              bestD = dist[j]
              bx = ox
              by = oy
            }
          }
        }
        if (bx !== 0 || by !== 0) {
          const l = Math.hypot(bx, by)
          fx[i] = bx / l
          fy[i] = by / l
        }
      }
    }
    this.flowValid = true
  }

  /** Direction toward the player from this world point. Returns false if unreachable. */
  flowAt(x: number, y: number, out: Vec): boolean {
    const tx = Math.floor(x / TILE)
    const ty = Math.floor(y / TILE)
    if (!this.flowValid || !this.inBounds(tx, ty)) {
      out.x = 0
      out.y = 0
      return false
    }
    const i = ty * this.w + tx
    if (this.dist[i] < 0) {
      out.x = 0
      out.y = 0
      return false
    }
    out.x = this.flowX[i]
    out.y = this.flowY[i]
    return out.x !== 0 || out.y !== 0
  }

  /** Tile steps to the player, or -1 if unreachable. */
  distanceAt(x: number, y: number): number {
    const tx = Math.floor(x / TILE)
    const ty = Math.floor(y / TILE)
    if (!this.flowValid || !this.inBounds(tx, ty)) return -1
    return this.dist[ty * this.w + tx]
  }

  // ------------------------------------------------------------- spawning

  /**
   * A free, player-reachable floor point between minD and maxD of (x, y).
   * Returns false rather than falling back to "anywhere": a generator sealed
   * behind a locked gate must not teleport its pours into the open room.
   */
  findOpenNear(x: number, y: number, minD: number, maxD: number, rng: Rng, out: Vec, radius = 10): boolean {
    for (let attempt = 0; attempt < 40; attempt++) {
      const a = rng.angle()
      const d = rng.range(minD, maxD)
      const px = x + Math.cos(a) * d
      const py = y + Math.sin(a) * d
      if (px < TILE || py < TILE || px > this.pixelW - TILE || py > this.pixelH - TILE) continue
      if (!this.circleFree(px, py, radius)) continue
      if (this.distanceAt(px, py) < 0 && this.flowValid) continue
      out.x = px
      out.y = py
      return true
    }
    return false
  }

  findAnyOpen(rng: Rng, out: Vec, radius = 10): boolean {
    for (let attempt = 0; attempt < 64; attempt++) {
      const tx = rng.int(1, this.w - 2)
      const ty = rng.int(1, this.h - 2)
      const px = tx * TILE + TILE / 2
      const py = ty * TILE + TILE / 2
      if (!this.circleFree(px, py, radius)) continue
      out.x = px
      out.y = py
      return true
    }
    out.x = this.pixelW / 2
    out.y = this.pixelH / 2
    return false
  }

  // ------------------------------------------------------------ tile layer

  /** World-space torch positions baked into the current layer (flicker pass reads these). */
  readonly torches: Vec[] = []

  /** Composite the whole room's floor+walls once; the frame just blits a slice. */
  bakeLayer(pal: ZonePalette): void {
    const c = document.createElement('canvas')
    c.width = this.pixelW
    c.height = this.pixelH
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.imageSmoothingEnabled = false
    const floor = spriteCanvas('tile_floor')
    const floorAlt = spriteCanvas('tile_floor_alt')
    const wall = spriteCanvas('tile_wall')
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        const i = y * this.w + x
        const g = this.glyphs[i]
        const dx = x * TILE
        const dy = y * TILE
        if (g === '#') {
          if (wall) ctx.drawImage(wall, dx, dy, TILE, TILE)
        } else {
          const useAlt = ((x * 7 + y * 13) % 11) === 0
          const src = useAlt ? floorAlt ?? floor : floor
          if (src) ctx.drawImage(src, dx, dy, TILE, TILE)
        }
      }
    }
    // Contact shadow under every wall face — cheap depth, big readability win.
    ctx.fillStyle = 'rgba(0,0,0,0.32)'
    for (let y = 0; y < this.h - 1; y++) {
      for (let x = 0; x < this.w; x++) {
        if (this.glyphs[y * this.w + x] === '#' && this.glyphs[(y + 1) * this.w + x] !== '#') {
          ctx.fillRect(x * TILE, (y + 1) * TILE, TILE, 7)
        }
      }
    }

    // Wall torches (zone identity: "warm torchlight vs cool stone"). Sconces
    // bake onto south-facing wall tiles on a deterministic lattice; the live
    // flicker glow is drawn per-frame from `torches` by renderTorchlight.
    this.torches.length = 0
    const density = pal.torches ?? 0
    if (density > 0) {
      for (let y = 0; y < this.h - 1; y++) {
        for (let x = 0; x < this.w; x++) {
          if (this.glyphs[y * this.w + x] !== '#') continue
          if (this.glyphs[(y + 1) * this.w + x] === '#') continue
          const h = ((x * 73856093) ^ (y * 19349663)) >>> 0
          if ((h % 1000) / 1000 >= density) continue
          const px = x * TILE + TILE / 2
          const py = y * TILE + TILE * 0.72
          // bracket
          ctx.fillStyle = '#1c1610'
          ctx.fillRect(px - 2, py - 2, 4, 9)
          // flame (two-tone, chunky)
          ctx.fillStyle = pal.glow
          ctx.fillRect(px - 3, py - 9, 6, 7)
          ctx.fillStyle = '#fff3c4'
          ctx.fillRect(px - 1, py - 7, 2, 3)
          this.torches.push({ x: px, y: py - 6 })
        }
      }
    }
    this.layer = c
  }
}

// ---------------------------------------------------------------- spatial hash

export class SpatialHash {
  private cell = 48
  private cols = 0
  private rows = 0
  private heads = new Int32Array(0)
  private next = new Int32Array(0)
  private xs = new Float32Array(0)
  private ys = new Float32Array(0)
  private count = 0

  configure(pixelW: number, pixelH: number, cell = 48): void {
    this.cell = cell
    this.cols = Math.max(1, Math.ceil(pixelW / cell))
    this.rows = Math.max(1, Math.ceil(pixelH / cell))
    const n = this.cols * this.rows
    if (this.heads.length !== n) this.heads = new Int32Array(n)
  }

  build(items: readonly { pos: Vec }[]): void {
    const n = items.length
    if (this.next.length < n) {
      this.next = new Int32Array(Math.max(64, n * 2))
      this.xs = new Float32Array(Math.max(64, n * 2))
      this.ys = new Float32Array(Math.max(64, n * 2))
    }
    this.heads.fill(-1)
    this.count = n
    for (let i = 0; i < n; i++) {
      const p = items[i].pos
      this.xs[i] = p.x
      this.ys[i] = p.y
      const cx = Math.max(0, Math.min(this.cols - 1, (p.x / this.cell) | 0))
      const cy = Math.max(0, Math.min(this.rows - 1, (p.y / this.cell) | 0))
      const c = cy * this.cols + cx
      this.next[i] = this.heads[c]
      this.heads[c] = i
    }
  }

  /** Visit candidate indices within r of (x,y). cb may early-out by returning true. */
  query(x: number, y: number, r: number, cb: (i: number) => boolean | void): void {
    if (this.count === 0) return
    const c0x = Math.max(0, Math.min(this.cols - 1, ((x - r) / this.cell) | 0))
    const c1x = Math.max(0, Math.min(this.cols - 1, ((x + r) / this.cell) | 0))
    const c0y = Math.max(0, Math.min(this.rows - 1, ((y - r) / this.cell) | 0))
    const c1y = Math.max(0, Math.min(this.rows - 1, ((y + r) / this.cell) | 0))
    const r2 = r * r
    for (let cy = c0y; cy <= c1y; cy++) {
      for (let cx = c0x; cx <= c1x; cx++) {
        let i = this.heads[cy * this.cols + cx]
        while (i !== -1) {
          const dx = this.xs[i] - x
          const dy = this.ys[i] - y
          if (dx * dx + dy * dy <= r2) {
            if (cb(i) === true) return
          }
          i = this.next[i]
        }
      }
    }
  }
}
